import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { pillars } from '../data/pillars';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_rejuven-health/artifacts/rqq06olv_Rejuven%20Logo%20-%20Website.png';

const linkCls = 'text-[#6C6361] hover:text-[#2B2625] transition-colors text-sm font-medium';

export const SiteNav = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [useCasesOpen, setUseCasesOpen] = useState(false);
  const [mobileUseCases, setMobileUseCases] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white/75 backdrop-blur-2xl border-b border-[#E8D5D1]/60">
      <nav className="max-w-6xl mx-auto px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="nav-logo"><img src={LOGO_URL} alt="Rejuven" className="h-11 lg:h-12" /></Link>

          {/* Desktop */}
          <div className="hidden lg:flex items-center gap-8">
            <div className="relative" onMouseEnter={() => setUseCasesOpen(true)} onMouseLeave={() => setUseCasesOpen(false)}>
              <button className={`flex items-center gap-1 ${linkCls}`} onClick={() => setUseCasesOpen(true)} data-testid="nav-usecases-trigger">
                Use Cases <ChevronDown className={`w-4 h-4 transition-transform ${useCasesOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {useCasesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.15 }}
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[30rem]"
                    data-testid="nav-usecases-dropdown"
                  >
                    <div className="bg-white rounded-2xl border border-[#E8D5D1] shadow-[0_20px_50px_-20px_rgba(43,38,37,0.2)] p-3 grid grid-cols-2 gap-1">
                      {pillars.map((p) => {
                        const Icon = p.icon;
                        return (
                          <Link key={p.slug} to={`/pillars/${p.slug}`} onClick={() => setUseCasesOpen(false)} className="flex items-start gap-3 p-3 rounded-xl hover:bg-[#F3EFEF] transition-colors" data-testid={`nav-usecase-${p.slug}`}>
                            <div className="w-9 h-9 rounded-lg bg-[#F3EFEF] flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-[#C28B87]" strokeWidth={1.7} /></div>
                            <div>
                              <p className="text-sm font-medium text-[#2B2625] leading-tight">{p.title}</p>
                              <p className="text-xs text-[#6C6361] leading-snug mt-0.5 line-clamp-2">{p.shortDesc}</p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <Link to="/services" className={linkCls} data-testid="nav-services">Services</Link>
            <Link to="/about" className={linkCls} data-testid="nav-about">About</Link>
            <Link to="/contact" className={linkCls} data-testid="nav-contact">Contact</Link>
          </div>

          <div className="hidden lg:flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" className="text-[#2B2625] hover:bg-[#F3EFEF] rounded-full px-5" data-testid="nav-signin-btn">Sign In</Button></Link>
            <Link to="/?waitlist=true"><Button className="bg-[#C28B87] text-white hover:bg-[#b07b77] rounded-full px-6 shadow-[0_6px_20px_rgba(194,139,135,0.35)] transition-transform hover:scale-[1.02]" data-testid="nav-waitlist-btn">Join the Waitlist</Button></Link>
          </div>

          <button className="lg:hidden p-2" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
            {mobileOpen ? <X className="w-6 h-6 text-[#2B2625]" /> : <Menu className="w-6 h-6 text-[#2B2625]" />}
          </button>
        </div>

        {/* Mobile */}
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="lg:hidden mt-3 bg-white rounded-2xl border border-[#E8D5D1] shadow-lg overflow-hidden">
            <div className="px-5 py-5 space-y-1">
              <button className="w-full flex items-center justify-between py-2 text-[#2B2625] font-medium" onClick={() => setMobileUseCases((v) => !v)} data-testid="mobile-usecases-trigger">
                Use Cases <ChevronDown className={`w-4 h-4 transition-transform ${mobileUseCases ? 'rotate-180' : ''}`} />
              </button>
              {mobileUseCases && (
                <div className="pl-3 pb-2 space-y-1 border-l border-[#E8D5D1]">
                  {pillars.map((p) => (
                    <Link key={p.slug} to={`/pillars/${p.slug}`} onClick={() => setMobileOpen(false)} className="block py-1.5 text-sm text-[#6C6361] hover:text-[#2B2625]" data-testid={`mobile-usecase-${p.slug}`}>{p.title}</Link>
                  ))}
                </div>
              )}
              <Link to="/services" onClick={() => setMobileOpen(false)} className="block py-2 text-[#2B2625] font-medium">Services</Link>
              <Link to="/about" onClick={() => setMobileOpen(false)} className="block py-2 text-[#2B2625] font-medium">About</Link>
              <Link to="/contact" onClick={() => setMobileOpen(false)} className="block py-2 text-[#2B2625] font-medium">Contact</Link>
              <div className="pt-4 space-y-3 border-t border-[#E8D5D1]">
                <Link to="/?waitlist=true" onClick={() => setMobileOpen(false)} className="block"><Button className="w-full bg-[#C28B87] text-white hover:bg-[#b07b77] rounded-full">Join the Waitlist</Button></Link>
                <Link to="/login" onClick={() => setMobileOpen(false)} className="block"><Button variant="outline" className="w-full rounded-full border-[#E8D5D1] text-[#2B2625]">Sign In</Button></Link>
              </div>
            </div>
          </motion.div>
        )}
      </nav>
    </header>
  );
};
